import type { FC } from "react";

import { NavRow } from "@/components/ui/nav-row";
import { PaneHeader } from "@/components/ui/pane-header";
import { cn } from "@/lib/utils";

import type { CourseSection } from "../catalog";

type Props = {
  sections: CourseSection[];
  activeId: string;
  onSelect: (id: string) => void;
};

const LessonNumber: FC<{ n: number; active?: boolean; muted?: boolean }> = ({
  n,
  active,
  muted
}) => (
  <span
    aria-hidden
    className={cn(
      "flex size-4.5 shrink-0 items-center justify-center rounded-xs text-xs tabular-nums",
      active ? "bg-fill-3 text-fg" : "bg-fill-2 text-fg-3",
      muted && "bg-fill-1 text-fg-4"
    )}
  >
    {n}
  </span>
);

/**
 * The course outline: a section per topic, tutorials numbered in order, then
 * the ones still in production as Coming Soon.
 */
export const CourseSidebar: FC<Props> = ({ sections, activeId, onSelect }) => (
  <aside className="hidden w-60 shrink-0 flex-col border-r border-line-2 md:flex">
    <PaneHeader title="Tutorials" />
    <nav
      aria-label="Course"
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-2"
    >
      {sections.map((s) => (
        <section
          key={s.topic}
          aria-label={s.topic}
          className="flex flex-col gap-0.5"
        >
          <h2 className="px-2 pt-1 pb-1.5 text-sm text-fg-3">{s.topic}</h2>
          {s.tutorials.map((t, i) => (
            <NavRow key={t.id} asChild active={t.id === activeId}>
              <button type="button" onClick={() => onSelect(t.id)}>
                <LessonNumber n={i + 1} active={t.id === activeId} />
                <span className="min-w-0 flex-1 truncate">{t.title}</span>
              </button>
            </NavRow>
          ))}
          {s.upcoming.map((u, i) => (
            <NavRow
              key={u.id}
              asChild
              className="cursor-default text-fg-4 disabled:hover:bg-transparent disabled:hover:text-fg-4"
            >
              <button type="button" disabled data-testid="tutorial-upcoming">
                <LessonNumber n={s.tutorials.length + i + 1} muted />
                <span className="min-w-0 flex-1 truncate">{u.title}</span>
                <span className="shrink-0 text-sm">Soon</span>
              </button>
            </NavRow>
          ))}
        </section>
      ))}
    </nav>
  </aside>
);
